import { baseApi as api } from './api.base.js';
const injectedRtkApi = api.injectEndpoints({
  endpoints: (build) => ({
    apiControllerFormatAddress: build.query<
      ApiControllerFormatAddressApiResponse,
      ApiControllerFormatAddressApiArg
    >({
      query: (queryArg) => ({ url: `/loan/format/${queryArg.address}` }),
    }),
    apiControllerLinkColdWallet: build.mutation<
      ApiControllerLinkColdWalletApiResponse,
      ApiControllerLinkColdWalletApiArg
    >({
      query: (queryArg) => ({
        url: `/loan/cold-wallets/link`,
        method: 'POST',
        body: queryArg.linkDto,
      }),
    }),
    apiControllerUnlinkColdWallet: build.mutation<
      ApiControllerUnlinkColdWalletApiResponse,
      ApiControllerUnlinkColdWalletApiArg
    >({
      query: (queryArg) => ({
        url: `/loan/cold-wallets/unlink`,
        method: 'POST',
        body: queryArg.unlinkDto,
      }),
    }),
    apiControllerGetColdWallets: build.query<
      ApiControllerGetColdWalletsApiResponse,
      ApiControllerGetColdWalletsApiArg
    >({
      query: (queryArg) => ({
        url: `/loan/cold-wallets/list/${queryArg.projectId}`,
      }),
    }),
    apiControllerUpdateColdWallet: build.mutation<
      ApiControllerUpdateColdWalletApiResponse,
      ApiControllerUpdateColdWalletApiArg
    >({
      query: (queryArg) => ({
        url: `/loan/cold-wallets/update/${queryArg.walletId}`,
        method: 'PUT',
        body: queryArg.updateDto,
      }),
    }),
    apiControllerAddSnapshot: build.mutation<
      ApiControllerAddSnapshotApiResponse,
      ApiControllerAddSnapshotApiArg
    >({
      query: (queryArg) => ({
        url: `/loan/cold-wallets/snapshots/add`,
        method: 'POST',
        body: queryArg.snapshotDto,
      }),
    }),
    apiControllerGetSnapshots: build.query<
      ApiControllerGetSnapshotsApiResponse,
      ApiControllerGetSnapshotsApiArg
    >({
      query: (queryArg) => ({
        url: `/loan/cold-wallets/snapshots/wallet/${queryArg.walletId}`,
        params: {
          startDate: queryArg.startDate,
          endDate: queryArg.endDate,
        },
      }),
    }),
    apiControllerGetSnapshotsByProject: build.query<
      ApiControllerGetSnapshotsByProjectApiResponse,
      ApiControllerGetSnapshotsByProjectApiArg
    >({
      query: (queryArg) => ({
        url: `/loan/cold-wallets/snapshots/project/${queryArg.projectId}`,
        params: {
          startDate: queryArg.startDate,
          endDate: queryArg.endDate,
        },
      }),
    }),
  }),
  overrideExisting: false,
});
export { injectedRtkApi as generatedApi };
export type ApiControllerFormatAddressApiResponse =
  /** status 200 Returns formatted address */ LoanFormatAddressResponse;
export type ApiControllerFormatAddressApiArg = {
  /** Wallet address to format */
  address: string;
};
export type ApiControllerLinkColdWalletApiResponse =
  /** status 201 Returns linking result */ LoanLinkColdWalletResponse;
export type ApiControllerLinkColdWalletApiArg = {
  /** Cold wallet linking data */
  linkDto: LinkDto;
};
export type ApiControllerUnlinkColdWalletApiResponse =
  /** status 200 Returns unlinking result */ LoanStatusResponse;
export type ApiControllerUnlinkColdWalletApiArg = {
  /** Cold wallet unlinking data */
  unlinkDto: UnlinkDto;
};
export type ApiControllerGetColdWalletsApiResponse =
  /** status 200 Returns list of cold wallets associated with the project */ LoanColdWalletsListResponse;
export type ApiControllerGetColdWalletsApiArg = {
  /** Project identifier */
  projectId: number;
};
export type ApiControllerUpdateColdWalletApiResponse =
  /** status 200 Returns update result */ LoanColdWalletResponse;
export type ApiControllerUpdateColdWalletApiArg = {
  /** Wallet identifier */
  walletId: number;
  /** Updated wallet data */
  updateDto: UpdateDto;
};
export type ApiControllerAddSnapshotApiResponse =
  /** status 201 Returns snapshot creation result */ LoanAddSnapshotsResponse;
export type ApiControllerAddSnapshotApiArg = {
  /** Wallets to snapshot */
  snapshotDto: SnapshotDto;
};
export type ApiControllerGetSnapshotsApiResponse =
  /** status 200 Returns wallet balance snapshots */ LoanSnapshotsResponse;
export type ApiControllerGetSnapshotsApiArg = {
  /** Wallet identifier */
  walletId: number;
  /** Start date (YYYY-MM-DD) */
  startDate?: string;
  /** End date (YYYY-MM-DD) */
  endDate?: string;
};
export type ApiControllerGetSnapshotsByProjectApiResponse =
  /** status 200 Returns project balance snapshots across all wallets */ LoanSnapshotsResponse;
export type ApiControllerGetSnapshotsByProjectApiArg = {
  /** Project identifier */
  projectId: number;
  /** Start date (YYYY-MM-DD) */
  startDate?: string;
  /** End date (YYYY-MM-DD) */
  endDate?: string;
};
export type LoanFormatAddressResponse = {
  /** Operation status */
  status: boolean;
  /** Normalized address (EVM checksum / Bittensor SS58 / Solana base58) */
  data: string;
};
export type LoanAssetItem = {
  /** Source blockchain */
  blockchain?: string;
  /** Token symbol */
  tokenSymbol?: string;
  /** Token name */
  tokenName?: string;
  /** Token contract address (absent for native) */
  contractAddress?: string;
  /** Human-readable balance */
  balance?: string;
  /** Balance value in USD */
  balanceUsd?: number;
  /** Unit price in USD */
  tokenPrice?: number;
};
export type LoanBalancesData = {
  /** Assets with positive USD value */
  assets: LoanAssetItem[];
  /** Sum of all asset USD values */
  totalBalanceUsd: number;
};
export type LoanLinkColdWalletData = {
  /** Created cold wallet ID */
  id: number;
  /** Balances fetched at link time */
  balances: LoanBalancesData;
};
export type LoanLinkColdWalletResponse = {
  /** Operation status */
  status: boolean;
  data: LoanLinkColdWalletData;
};
export type LinkDto = {};
export type LoanStatusResponse = {
  /** Operation status */
  status: boolean;
  /** Optional info/error message */
  message?: string;
};
export type UnlinkDto = {};
export type LoanColdWalletItem = {
  /** Cold wallet ID */
  id: number;
  /** Project ID */
  projectId: number;
  /** Wallet address (EVM checksum / SS58 / base58) */
  address: string;
  /** Optional wallet name */
  name?: object | null;
  /** Cached balances JSON string */
  balances?: object | null;
  /** Date linked */
  added: string;
  /** Date last updated */
  updated: string;
  /** Ready-to-open explorer URL for the address (by address type), or null if the explorer chain is unknown */
  scanUrl: object | null;
};
export type LoanColdWalletsListData = {
  list: LoanColdWalletItem[];
  /** Unix seconds of the last balances refresh */
  updateTimeSec?: object | null;
};
export type LoanColdWalletsListResponse = {
  /** Operation status */
  status: boolean;
  data: LoanColdWalletsListData;
};
export type LoanColdWalletResponse = {
  /** Operation status */
  status: boolean;
  data: LoanColdWalletItem;
};
export type UpdateDto = {};
export type LoanAddSnapshotItem = {
  /** Created snapshot ID (on success) */
  id?: number;
  /** Wallet ID (present on error rows) */
  walletId?: number;
  /** Snapshotted balances (on success) */
  balances?: LoanBalancesData;
  /** Error message (on failure rows) */
  error?: string;
};
export type LoanAddSnapshotsResponse = {
  /** Operation status */
  status: boolean;
  data: LoanAddSnapshotItem[];
};
export type SnapshotDto = {};
export type LoanSnapshotItem = {
  /** Snapshot ID */
  id: number;
  /** Cold wallet ID */
  walletId: number;
  /** Balances JSON string at snapshot time */
  balances?: object | null;
  /** Snapshot date */
  date: string;
};
export type LoanSnapshotsResponse = {
  /** Operation status */
  status: boolean;
  data: LoanSnapshotItem[];
};
